import ExclusivesNavigator from "@components/Exclusives/Navigator";
import LBox from "@components/LBox";
import LPressable from "@components/LPressable";
import LText from "@components/LText";
// import LBox from "@components/LBox";
// import LText from "@components/LText";
import { CollectionTitle } from "@components/TopBar/CollectionTitle";
import PageTitle from "@components/TopBar/PageTitle";
import { TopBarProps } from "@components/TopBar/types";
import { router } from "expo-router";
import React from "react";
import { Platform } from "react-native";
import type { Segments } from "@utils/navigation/types";
import { useNavigationStore } from "@utils/navigation/useNavigationStore";

const ExploreTopBar = ({ segments }: { segments: Segments }) => {
  const isAccess = segments.includes("access" as never);
  return (
    <LBox
      flexDirection="row"
      alignItems="center"
      justifyContent="center"
      gap="s4"
    >
      <LPressable
        sentryEventName="explore-top-bar-music"
        onPress={() => router.replace("/(app)/(tabs)/explore")}
      >
        <LBox alignItems="center" gap="s1">
          <LText
            variant="h1"
            fontWeight="700"
            color={isAccess ? "navigatorInactiveLabel" : "white"}
          >
            Music
          </LText>
          <LBox
            width={40}
            height={2}
            backgroundColor="white"
            borderRadius="xs"
            opacity={isAccess ? 0 : 1}
          />
        </LBox>
      </LPressable>
      <>
        <LPressable
          sentryEventName="explore-top-bar-access"
          onPress={() => {
            router.replace("/(app)/(tabs)/explore/access");
          }}
        >
          <LBox alignItems="center" gap="s1">
            <LText
              variant="h1"
              fontWeight="700"
              color={isAccess ? "white" : "navigatorInactiveLabel"}
            >
              Access
            </LText>
            <LBox
              width={40}
              height={2}
              backgroundColor="white"
              borderRadius="xs"
              opacity={isAccess ? 1 : 0}
            />
          </LBox>
        </LPressable>
      </>
    </LBox>
  );
};

const defaultOptions = (title?: string): TopBarProps => {
  return {
    middle: {
      component: <PageTitle title={title} />,
      containerProps: {
        alignItems: "center",
        justifyContent: "center",
      },
    },
  };
};

export const getTopBarOptions = (
  pathname: string,
  segments: Segments,
  previousPathname?: string,
  previousSegments?: Segments,
): TopBarProps => {
  const isWeb = Platform.OS === "web";

  if (segments.at(2) === "profile") {
    if (
      segments.includes("[artistId]" as never) ||
      segments.includes("[collectionId]" as never)
    ) {
      return {
        middle: {
          component: <CollectionTitle />,
          containerProps: {
            alignItems: "center",
            justifyContent: "center",
          },
        },
        leftSide: { buttons: ["back"] },
        rightSide: { buttons: [isWeb ? "view-app" : "inbox"] },
      };
    }
  }

  if (pathname.includes("store")) {
    return {
      hidden: true,
      leftSide: { buttons: [] },
      middle: { containerProps: { flex: 0 } },
      rightSide: { buttons: [] },
    };
  }

  switch (pathname) {
    case "/profile/artist/dashboard":
    case "/profile/artist/accesses":
    case "/profile/experiences":
    case "/library":
    case "/profile/you":
    case "/profile/wallet":
      return {
        leftSide: {},
        middle: { containerProps: { flex: 0 } },
        rightSide: {
          buttons: isWeb ? ["settings"] : ["inbox", "settings"],
          containerProps: { gap: "s2" },
        },
      };
    case "/profile/settings":
      return {
        ...defaultOptions("Settings"),
        rightSide: { buttons: ["intercom"] },
      };
    case "/profile/artist/settings":
      return {
        ...defaultOptions("Artist settings"),
        leftSide: { buttons: ["back"] },
        rightSide: { buttons: ["intercom"] },
      };
    case "/profile/edit":
      return {
        ...defaultOptions("Edit Profile"),
        rightSide: { buttons: [] },
      };
    case "/profile/artist/profile":
      return {
        ...defaultOptions("Edit artist profile"),
        leftSide: { buttons: ["back"] },
        rightSide: { buttons: ["intercom"] },
      };
    // case "/play":
    //   return {
    //     ...defaultOptions(),
    //     leftSide: { buttons: [] },
    //   };
    // case "/play/manage":
    //   return {
    //     middle: {
    //       component: (
    //         <LBox flex={1} alignItems="center" justifyContent="center">
    //           <LText
    //             textAlign="center"
    //             fontWeight={600}
    //             fontSize={16}
    //             numberOfLines={1}
    //           >
    //             Manage roster
    //           </LText>
    //         </LBox>
    //       ),
    //     },
    //   };
    case "/home": {
      const options: TopBarProps = {
        leftSide: { buttons: [] },
        rightSide: { buttons: [isWeb ? "view-app" : "inbox"] },
        middle: {
          component: <ExclusivesNavigator />,
        },
      };

      return options;
    }
    case "/inbox":
      return {
        hidden: true,
      };
    case pathname.match(/^\/inbox\/[^/]+$/)?.input:
      return {
        hidden: true,
      };
    case pathname.match(
      /^\/(home|experiences|explore|library)\/(artist|album|playlist)\//,
    )?.input:
      return {
        leftSide: isWeb ? { buttons: ["back"] } : undefined,
        middle: {
          component: <CollectionTitle />,
          containerProps: {
            alignItems: "center",
            justifyContent: "center",
          },
        },
        rightSide: { buttons: isWeb ? ["view-app"] : ["inbox"] },
      };
    case pathname.match(/\/experiences\/experience\//)?.input:
      return {
        ...defaultOptions("Experience detail"),
        rightSide: { buttons: [] },
      };
    case pathname.match(/^\/profile/)?.input: {
      // starting with /profile
      const profileTopBar: TopBarProps = {
        rightSide: {
          buttons: ["inbox", "settings"],
          containerProps: { gap: "s2" },
        },
      };

      return profileTopBar;
    }
    // case "/play/contests/discover":
    //   return defaultOptions("Discover Contests");

    case "/explore":
    case "/explore/access":
      return {
        middle: {
          component: <ExploreTopBar segments={segments} />,
          containerProps: {
            alignItems: "center",
            justifyContent: "center",
          },
        },
      };
    case pathname.match(/\/access\//)?.input: {
      // Find the first non-access route in history to retain its TopBar
      // Access store directly to avoid unnecessary re-renders in TopBar component
      const pathHistory = useNavigationStore.getState().pathHistory;
      const segmentsHistory = useNavigationStore.getState().segmentsHistory;

      // Iterate backwards through history to find first non-access route
      for (let i = pathHistory.length - 1; i >= 0; i--) {
        const historyPath = pathHistory[i];
        if (historyPath && !historyPath.includes("/access/")) {
          const historySegments = segmentsHistory[i];
          if (historySegments) {
            return getTopBarOptions(historyPath, historySegments);
          }
        }
      }

      // Fallback to previous pathname if history not available
      return previousPathname && previousSegments
        ? getTopBarOptions(previousPathname, previousSegments)
        : defaultOptions();
    }
    default:
      return defaultOptions();
  }
};
